<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Column from 'primevue/column'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import DataTable from '@lib/components/shared/DataTable.vue'
import EmptyState from '@lib/components/shared/EmptyState.vue'
import SkeletonRows from '@lib/components/shared/SkeletonRows.vue'
import ExpandRowPanel from '@lib/components/shared/ExpandRowPanel.vue'
import { useFilters, type FilterParser } from '@lib/composables/useFilters'
import { useConfirm } from '@lib/composables/useConfirm'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { useUsersData, type User, type UserFilters } from '@/composables/useUsersData'
import UsersFilterBar from '@/components/domain/users/UsersFilterBar.vue'
import UserEditPanel from '@/components/domain/users/UserEditPanel.vue'
import UserMobileCard from '@/components/domain/users/UserMobileCard.vue'
import type { LocationQueryValue } from 'vue-router'

const { t } = useI18n()
const ui = useUiStore()
const auth = useAuthStore()
const { confirm } = useConfirm()

const initialFilters: UserFilters = {
    search: '',
    active: 'all'
}

function asString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string {
    if (Array.isArray(raw)) return raw[0] ?? ''
    return raw ?? ''
}
function asActive(raw: LocationQueryValue | LocationQueryValue[] | undefined): UserFilters['active'] {
    const v = asString(raw)
    if (v === 'true' || v === 'false') return v
    return 'all'
}

const parsers: { [K in keyof UserFilters]?: FilterParser<UserFilters[K]> } = {
    active: asActive
}

const { filters } = useFilters<UserFilters>(initialFilters, { parsers })

const data = useUsersData()

watch(
    filters,
    v => {
        data.filters.value = { ...v }
    },
    { immediate: true, deep: true }
)

const expandedId = ref<number | null>(null)
// Both desktop (md+) and mobile (< md) branches render in the DOM (only one
// is visible per Tailwind hidden classes). Each branch has its own template
// ref to avoid v-for collisions; activeEditRef picks whichever is currently
// mounted with a panel inside. See EventsView.vue (commit 3a40bae).
type EditPanelHandle = { isDirty: () => boolean }
const editPanelRefDesktop = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
const editPanelRefMobile = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
function unwrap(r: EditPanelHandle | EditPanelHandle[] | null): EditPanelHandle | null {
    if (!r) return null
    if (Array.isArray(r)) return r.find(x => x != null) ?? null
    return r
}
function activeEditRef(): EditPanelHandle | null {
    return unwrap(editPanelRefMobile.value) ?? unwrap(editPanelRefDesktop.value)
}
function safeIsDirty(): boolean {
    try {
        return activeEditRef()?.isDirty() ?? false
    } catch {
        return false
    }
}

const expandedRows = computed<Record<string, boolean>>(() => {
    if (expandedId.value === null) return {}
    return { [String(expandedId.value)]: true }
})

const rows = computed<User[]>(() => data.filteredUsers.value)
const isEmpty = computed(() => !data.loading.value && rows.value.length === 0)

onMounted(async () => {
    ui.setBreadcrumbs([{ label: t('users.list.title') }])
    await data.refetch()
})

async function toggleExpand(id: number) {
    if (expandedId.value === id) {
        if (safeIsDirty()) {
            const ok = await confirm({
                title: t('users.confirm.unsavedChanges'),
                severity: 'danger'
            })
            if (!ok) return
        }
        expandedId.value = null
    } else {
        expandedId.value = id
    }
}

function onRowClick(row: User) {
    toggleExpand(row.id)
}

function clearFilters() {
    filters.value = { ...initialFilters }
}

async function onSaved() {
    expandedId.value = null
    await data.refetch()
}

async function onDeleted() {
    expandedId.value = null
    await data.refetch()
}

function onCancel() {
    expandedId.value = null
}

function displayName(u: User): string {
    const full = `${u.firstName} ${u.lastName}`.trim()
    if (full) return full
    if (u.username) return u.username
    return t('users.list.fullNameFallback')
}

function rowClass(u: User): string {
    return u.isActive ? '' : 'opacity-50'
}

watch(
    () => auth.invalidationKey,
    () => {
        expandedId.value = null
        data.refetch()
    }
)
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('users.list.title')" />

        <UsersFilterBar v-model="filters" @clear="clearFilters" />

        <div class="bg-white border border-gray-200 rounded-12 overflow-hidden">
            <SkeletonRows v-if="data.loading.value && rows.length === 0" :rows="8" :columns="5" />
            <EmptyState v-else-if="isEmpty" :title="t('users.list.empty')" />
            <template v-else>
                <!--
                    Both branches render in the DOM (visibility gated by
                    hidden/md:block/md:hidden). Each owns its own
                    UserEditPanel ref to avoid template-ref collisions
                    across two trees.
                -->
                <!-- DESKTOP (md+): table -->
                <div class="hidden md:block">
                    <DataTable
                        :value="rows"
                        row-key="id"
                        :expanded-rows="expandedRows"
                        :row-class="rowClass"
                        @row-click="onRowClick"
                    >
                        <Column :header="t('users.list.cols.name')">
                            <template #body="{ data: row }">
                                <span class="font-medium text-ink">{{ displayName(row) }}</span>
                            </template>
                        </Column>
                        <Column :header="t('users.list.cols.username')">
                            <template #body="{ data: row }">
                                <span class="text-sm font-mono">{{ row.username }}</span>
                            </template>
                        </Column>
                        <Column :header="t('users.list.cols.email')">
                            <template #body="{ data: row }">
                                <span class="text-sm">{{ row.email }}</span>
                            </template>
                        </Column>
                        <Column :header="t('users.list.cols.active')" :style="{ width: '80px' }">
                            <template #body="{ data: row }">
                                <span v-if="row.isActive" class="text-teal">✓</span>
                                <span v-else class="text-gray-300">×</span>
                            </template>
                        </Column>
                        <Column :header="t('users.list.cols.admin')" :style="{ width: '90px' }">
                            <template #body="{ data: row }">
                                <span
                                    v-if="row.isSuperuser"
                                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-l text-orange-d"
                                >
                                    {{ t('users.list.cols.admin') }}
                                </span>
                            </template>
                        </Column>
                        <template #expansion="{ data: row }">
                            <ExpandRowPanel
                                :title="t('users.list.cols.name') + ': ' + displayName(row)"
                                @close="toggleExpand(row.id)"
                            >
                                <UserEditPanel
                                    ref="editPanelRefDesktop"
                                    :user="row"
                                    @saved="onSaved"
                                    @deleted="onDeleted"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                    </DataTable>
                </div>
                <!-- MOBILE (< md): vertical card list -->
                <ul class="md:hidden divide-y divide-gray-100">
                    <li v-for="row in rows" :key="row.id" class="p-2">
                        <UserMobileCard
                            :user="row"
                            :expanded="expandedId === row.id"
                            @toggle-expand="toggleExpand(row.id)"
                        />
                        <ExpandRowPanel
                            v-if="expandedId === row.id"
                            :title="t('users.list.cols.name') + ': ' + displayName(row)"
                            class="mt-2"
                            @close="toggleExpand(row.id)"
                        >
                            <UserEditPanel
                                ref="editPanelRefMobile"
                                :user="row"
                                @saved="onSaved"
                                @deleted="onDeleted"
                                @cancel="onCancel"
                            />
                        </ExpandRowPanel>
                    </li>
                </ul>
            </template>
        </div>
    </DefaultLayout>
</template>
